import { type PaletteKey, type PixelCanvas, sprite } from '../../../lib';
import { box, byRow, dots, geometry, outlined, round, taper } from '../effect-guns.sprite';

/**
 * The Censer (the Plague Doctor set's gun, ADR-0160 amended): a brass launcher that lobs vials of miasma. A stock of
 * leather-wrapped iron, a brass body with a pressure gauge on its side, a glass vial lying on its back full of violet
 * liquid that bubbles, a perforated brass dome (the censer) over the barrel smoking through its holes, a dark iron barrel
 * banded in brass narrowing to a brass mouth. Fired, the gauge's needle kicks and the dome's holes flare.
 */

const IRON = ['1', '2', '3', '4'] as const;
const GOLD = ['U', 'U', 'Y', 'y'] as const;

/** Bubbles rising in the vial, per frame: [x, y] (each a speck, a ring when it is low). */
const BUBBLES = [
  [
    [27, 12],
    [32, 10],
    [35, 11],
  ],
  [
    [27, 10],
    [32, 12],
    [35, 9],
  ],
  [
    [27, 9],
    [32, 11],
    [34, 12],
  ],
  [
    [28, 12],
    [31, 9],
    [34, 10],
  ],
] as const;

/** `frame` of 4 (the vial's bubbles and slosh, which hole of the dome glows); `fired` 0 at rest … 2 the shot's moment. */
const censer = (frame: number, fired = 0) =>
  outlined((c: PixelCanvas) => {
    // The stock: iron wrapped in dark leather, two brass rivets, a brass butt cap.
    box(c, 5, 16, 22, 25, [2, 1], byRow(16, 25, ['k', '1', '2', '3'], 1, 2), 'l');
    for (const x of [9, 13, 17]) c.vline(x, 17, 24, '1');
    box(c, 5, 17, 7, 24, [1], (_x, y) => (y < 19 ? 'y' : y > 22 ? 'U' : 'Y'), 'l');
    dots(c, 'Y', [
      [11, 20],
      [19, 20],
    ]);
    // The body: brass, lit along its top, darker down its belly, a riveted seam along it, engraved rings at its ends.
    box(
      c,
      22,
      14,
      44,
      26,
      [2, 1],
      (_x, y) => (y === 14 ? 'y' : y === 15 ? 'Y' : y < 23 ? 'U' : y < 25 ? 'n' : 'N'),
      'r',
    );
    c.hline(24, 34, 15, 'y');
    c.hline(23, 43, 21, 'n');
    for (const x of [26, 39, 42]) c.set(x, 22, 'Y');
    for (const x of [24, 43]) c.vline(x, 16, 24, x === 24 ? 'Y' : 'n');
    // A pressure gauge on its side: a pale dial in a brass ring, its needle (kicked over when it fires).
    for (let y = 15; y <= 21; y++)
      for (let x = 29; x <= 35; x++) {
        const r = Math.hypot(x + 0.5 - 32, y + 0.5 - 18);
        if (r <= 3.4) c.set(x, y, r > 2.3 ? (x + y < 50 ? 'y' : 'n') : x + y < 49 ? 'e' : 't');
      }
    const needle: readonly [number, number] = [31 + fired, 17];
    dots(c, 'R', [needle]);
    dots(c, 'N', [[32, 18]]);
    // The trigger.
    box(c, 31, 27, 34, 29, [0, 1], (_x, y) => (y === 27 ? '3' : '2'), 'r');
    // The vial lying on its back: glass, violet liquid sloshing in its lower half, bubbles rising.
    box(c, 23, 6, 37, 13, [2, 1], (x, y) => {
      const rim = y === 6 || y === 13 || x === 23 || x === 37;
      if (rim) return y < 9 && x < 34 ? '4' : '3';
      // Its surface rocking gently from end to end.
      const surface = 8 + Math.round(Math.sin(x * 0.35 + frame * 1.6) * 0.6) + (fired ? 1 : 0);
      if (y < surface) return y === 7 && x > 25 && x < 31 ? 'w' : y === 7 ? '3' : '2';
      if (y === surface) return 'i';
      return y >= 12 ? 'P' : 'p';
    });
    for (const [x, y] of BUBBLES[frame % 4] ?? BUBBLES[0])
      if (y > 8 + (fired ? 1 : 0)) {
        c.set(x, y, 'w');
        if (y >= 11) c.set(x + 1, y, 'i');
      }
    // Brass caps on its ends.
    for (const [x0, x1] of [
      [20, 22],
      [38, 40],
    ] as const)
      box(c, x0, 7, x1, 12, [1], (_x, y) => (y < 9 ? 'y' : y > 10 ? 'U' : 'Y'));
    // The barrel: dark iron narrowing to the muzzle, banded in brass, a brass mouth with the miasma in it.
    taper(c, 45, 58, 19.5, 4.2, 2.6, round(IRON));
    for (const x of [47, 53])
      taper(c, x, x, 19.5, 4.6 - (x - 45) * 0.12, 4.6 - (x - 45) * 0.12, round(GOLD as readonly PaletteKey[]));
    taper(c, 59, 61, 19.5, 2.9, 2.5, round(GOLD as readonly PaletteKey[]));
    dots(c, fired ? 'i' : 'p', [
      [61, 19],
      [61, 20],
    ]);
    // The censer: a perforated brass dome on the barrel's back, its holes aglow, one brighter in turn.
    for (let y = 9; y <= 15; y++)
      for (let x = 45; x <= 53; x++) {
        const r = Math.hypot((x + 0.5 - 49) / 4.5, (y + 0.5 - 15.5) / 6);
        if (r > 1) continue;
        c.set(x, y, r > 0.78 ? (x < 49 ? 'y' : 'U') : x + y < 59 ? 'Y' : 'U');
      }
    c.set(49, 8, 'Y').set(48, 8, 'y');
    const holes = [
      [47, 12],
      [49, 11],
      [51, 12],
      [48, 14],
      [50, 14],
    ] as const;
    holes.forEach(([x, y], i) => {
      c.set(x, y, fired ? (i % 2 ? 'w' : 'i') : i === frame % holes.length || i === (frame + 2) % 5 ? 'i' : 'P');
    });
  });

export default sprite({
  name: 'pollinator_plague',
  ...geometry,
  notes: `Censer (ADR-0160 amended, the Plague Doctor set's gun): a bronze launcher. A stock of iron wrapped in dark leather
(brass cap, two rivets), a bronze body (a riveted seam, a cream gauge with a red needle on its side), a glass vial lying
on its back full of violet liquid (its surface rocking, bubbles rising over 4 frames), a perforated brass dome over the
barrel (the censer: its holes aglow in turn; its flair smokes out of it), a dark iron barrel banded in brass narrowing to
a brass mouth with the miasma in it. Fired (3 frames): the needle kicks over, the dome's holes flare, the liquid drops.
Rubric (4×): S4 R4 P4 L4 C4 A4 F4. Weakest: the body is a big bronze block next to the dark Inferno and Tempest guns
(on the lane its bronze is near the ground's hue, its rim parts it); the gauge is 7 px, a dot at 1:1.`,
  anims: {
    idle: { fps: 6, frames: [censer(0), censer(1), censer(2), censer(3)] },
    fire: { fps: 20, loop: false, frames: [censer(0, 2), censer(1, 1), censer(0)] },
  },
});
